import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useProjectContext } from '../../../contexts/ProjectContext';
import { useDashboardScope } from '../../../contexts/DashboardScopeContext';
import {
    DASHBOARD_PROJECT_STATUS_OPTIONS,
    DASHBOARD_SCOPE_PRESET_OPTIONS,
    DEFAULT_DASHBOARD_PROJECT_ID,
    DEFAULT_DASHBOARD_PROJECT_STATUS,
    formatDashboardDateRangeLabel,
    getDashboardPeriodLabel,
    getDashboardProjectStatusLabel,
    hasActivePortfolioScopeFilters,
    isPresetDashboardTimeRange,
    normalizeCustomScopeDays,
    parseRollingDaysFromTimeRange,
} from '../../../utils/dashboardScope';

/**
 * Global dashboard scope: period presets, custom N days, project, and status.
 */
const DashboardScopeStrip = ({ testId = 'dashboard-scope-strip' }) => {
    const {
        timeRange,
        setTimeRange,
        dateRange,
        projectId,
        setProjectId,
        projectStatus,
        setProjectStatus,
        clearPortfolioFilters,
    } = useDashboardScope();
    const { projects = [], loading: projectsLoading } = useProjectContext();
    const [customDaysInput, setCustomDaysInput] = useState('');

    const projectOptions = useMemo(
        () => (Array.isArray(projects) ? projects : []),
        [projects]
    );

    const selectedProject = useMemo(() => {
        if (!projectId || projectId === DEFAULT_DASHBOARD_PROJECT_ID) {
            return null;
        }
        return projectOptions.find((project) => `${project.id}` === `${projectId}`) || null;
    }, [projectId, projectOptions]);

    const statusSelectDisabled = projectId && projectId !== DEFAULT_DASHBOARD_PROJECT_ID;

    useEffect(() => {
        const rollingDays = parseRollingDaysFromTimeRange(timeRange);
        if (rollingDays != null && !isPresetDashboardTimeRange(timeRange)) {
            setCustomDaysInput(`${rollingDays}`);
            return;
        }
        if (isPresetDashboardTimeRange(timeRange) && timeRange.endsWith('d')) {
            const presetDays = parseRollingDaysFromTimeRange(timeRange);
            setCustomDaysInput(presetDays != null ? `${presetDays}` : '');
            return;
        }
        setCustomDaysInput('');
    }, [timeRange]);

    const handlePresetChange = useCallback((_event, nextValue) => {
        if (nextValue) {
            setTimeRange(nextValue);
        }
    }, [setTimeRange]);

    const handleApplyCustomDays = useCallback(() => {
        const days = normalizeCustomScopeDays(customDaysInput);
        setTimeRange(`${days}d`);
        setCustomDaysInput(`${days}`);
    }, [customDaysInput, setTimeRange]);

    const resolvedRangeLabel = formatDashboardDateRangeLabel(dateRange);
    const periodLabel = getDashboardPeriodLabel(timeRange);
    const showClearPortfolio = hasActivePortfolioScopeFilters(projectId, projectStatus);

    return (
        <Box
            data-testid={testId}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                py: 1,
                px: { xs: 0.5, md: 0 },
                mb: 1,
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1.5,
                }}
            >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Dashboard period
                </Typography>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={isPresetDashboardTimeRange(timeRange) ? timeRange : false}
                    onChange={handlePresetChange}
                    aria-label="Dashboard time period presets"
                >
                    {DASHBOARD_SCOPE_PRESET_OPTIONS.map((option) => (
                        <ToggleButton
                            key={option.value}
                            value={option.value}
                            aria-label={option.label}
                        >
                            {option.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <Box
                    component="form"
                    onSubmit={(event) => {
                        event.preventDefault();
                        handleApplyCustomDays();
                    }}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                    <TextField
                        size="small"
                        type="number"
                        label="Custom days"
                        value={customDaysInput}
                        onChange={(event) => setCustomDaysInput(event.target.value)}
                        inputProps={{
                            min: 1,
                            max: 730,
                            'aria-label': 'Custom rolling window days',
                            'data-testid': 'dashboard-scope-custom-days-input',
                        }}
                        sx={{ width: 120 }}
                    />
                    <Button
                        type="submit"
                        variant="outlined"
                        size="small"
                        data-testid="dashboard-scope-custom-days-apply"
                    >
                        Apply
                    </Button>
                </Box>
                {periodLabel ? (
                    <Chip
                        size="small"
                        variant="outlined"
                        label={periodLabel}
                        data-testid="dashboard-scope-period-chip"
                    />
                ) : null}
                {resolvedRangeLabel && (
                    <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={resolvedRangeLabel}
                        data-testid="dashboard-scope-resolved-range-chip"
                    />
                )}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1.5,
                }}
                data-testid="dashboard-scope-portfolio-filters"
            >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Portfolio
                </Typography>
                <Autocomplete
                    size="small"
                    options={projectOptions}
                    loading={projectsLoading}
                    value={selectedProject}
                    onChange={(_event, value) => {
                        setProjectId(value?.id != null ? `${value.id}` : DEFAULT_DASHBOARD_PROJECT_ID);
                    }}
                    getOptionLabel={(option) => option?.name || ''}
                    isOptionEqualToValue={(option, value) => `${option?.id}` === `${value?.id}`}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Project"
                            placeholder="All projects"
                            inputProps={{
                                ...params.inputProps,
                                'data-testid': 'dashboard-scope-project-select',
                            }}
                        />
                    )}
                    sx={{ minWidth: 220 }}
                />
                <FormControl size="small" sx={{ minWidth: 160 }} disabled={statusSelectDisabled}>
                    <InputLabel id="dashboard-scope-status-label">Status</InputLabel>
                    <Select
                        labelId="dashboard-scope-status-label"
                        label="Status"
                        value={projectStatus || DEFAULT_DASHBOARD_PROJECT_STATUS}
                        onChange={(event) => setProjectStatus(event.target.value)}
                        inputProps={{ 'data-testid': 'dashboard-scope-status-select' }}
                    >
                        {DASHBOARD_PROJECT_STATUS_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {statusSelectDisabled && (
                    <Typography variant="caption" color="text.secondary">
                        Status applies to all projects; clear project to filter by status.
                    </Typography>
                )}
                {showClearPortfolio && (
                    <Button
                        size="small"
                        variant="text"
                        onClick={clearPortfolioFilters}
                        data-testid="dashboard-scope-clear-portfolio-filters"
                    >
                        Clear portfolio filters
                    </Button>
                )}
                {showClearPortfolio && (
                    <Chip
                        size="small"
                        variant="outlined"
                        label={[
                            projectId !== DEFAULT_DASHBOARD_PROJECT_ID && selectedProject?.name
                                ? selectedProject.name
                                : null,
                            projectStatus !== DEFAULT_DASHBOARD_PROJECT_STATUS
                                ? getDashboardProjectStatusLabel(projectStatus)
                                : null,
                        ].filter(Boolean).join(' · ') || 'Filtered'}
                        data-testid="dashboard-scope-portfolio-chip"
                    />
                )}
            </Box>
        </Box>
    );
};

export default DashboardScopeStrip;
